import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { useTheme } from "../../context/ThemeContext";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
  getInputStyles,
} from "../../utils/themeUtils";
import { sendAnonAiChatMessage, AnonChatFieldRequest } from "../../api/publicAiChatAPI";
import { getTenantId } from "../../api/apiHelpers";
import AnonChatStructuredField from "./AnonChatStructuredField";

interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: number;
}

const SESSION_STORAGE_KEY = "anon_ai_session_id";
const LEAD_STORAGE_KEY = "anon_ai_lead_id";
// When set, FloatingChatGate keeps this widget mounted even after
// the visitor signs in mid-chat — preserves conversation continuity
// across the auth boundary. Cleared when the chat closes.
const ACTIVE_SESSION_KEY = "anon_ai_active_session";

interface AnonymousAIChatProps {
  /**
   * Business name from public contact-settings — used in the greeting.
   */
  businessName?: string;
  /**
   * Tenant-editable disclosure shown as a banner inside the panel
   * (e.g. "this conversation may be reviewed", PII reminders).
   * Optional; pass null/empty to hide.
   */
  disclosureText?: string | null;
}

// Brand colors via --color-primary CSS var. See CLAUDE.md.

/**
 * Anonymous AI customer-service chat for non-authenticated visitors.
 *
 * Single-purpose UX: floating button → one chat thread, no tabs / no
 * cards / no archived list. AI silently checks if a provided email
 * matches a member; if so, it tells the visitor to sign in (with a
 * sign-in CTA surfaced by the parent via should_redirect_to_signin).
 * Otherwise it captures lead information conversationally in the
 * registration-wizard order.
 *
 * Session id + (when created) lead id persist in sessionStorage so a
 * page refresh continues the same conversation.
 */
const AnonymousAIChat: React.FC<AnonymousAIChatProps> = ({ businessName, disclosureText }) => {
  const { isDarkMode } = useTheme();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() =>
    sessionStorage.getItem(SESSION_STORAGE_KEY)
  );
  const [leadId, setLeadId] = useState<string | null>(() =>
    sessionStorage.getItem(LEAD_STORAGE_KEY)
  );
  const [showSignInCta, setShowSignInCta] = useState(false);
  const [closed, setClosed] = useState(false);
  // Backend can request a structured field below the latest assistant
  // message — the visitor fills a real <input> instead of typing
  // unstructured text. Far more reliable than parsing names / emails
  // / dates from free-form replies.
  const [pendingField, setPendingField] = useState<AnonChatFieldRequest | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const accentColor = "var(--cc-action)";

  // Set the active-session flag whenever the chat is open — tells
  // FloatingChatGate to keep AnonymousAIChat rendered even if auth
  // state flips mid-conversation (so signing in via the inline form
  // doesn't unmount the chat). Cleared on close.
  useEffect(() => {
    if (open) {
      try { sessionStorage.setItem(ACTIVE_SESSION_KEY, "1"); } catch {}
    }
  }, [open]);

  // Greeting shown the first time the visitor opens the panel. Worded
  // to surface our two most common visitor intents so they can pick a
  // path quickly — open-ended "what brings you here?" greetings made
  // visitors stall waiting for the AI to lead.
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([
        {
          id: `g-${Date.now()}`,
          role: "assistant",
          content: businessName
            ? `Hi! Thanks for stopping by ${businessName}. Are you looking to sign up, schedule a trial, or just have a question about our programs?`
            : `Hi! Are you looking to sign up, schedule a trial, or just have a question?`,
          timestamp: Date.now(),
        },
      ]);
    }
  }, [open, businessName, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const tenantId = getTenantId();
  const signInPath = tenantId ? `/${tenantId}/login` : "/login";

  /**
   * Reset the widget to a fresh-start state when the visitor wants to
   * start a new conversation after one has ended (escalated, capped,
   * or otherwise closed). Clears the prior session id + lead id from
   * sessionStorage so the next outbound message creates a brand new
   * conversation row on the backend.
   */
  const handleStartNewChat = useCallback(() => {
    try {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      sessionStorage.removeItem(LEAD_STORAGE_KEY);
    } catch {}
    setSessionId(null);
    setLeadId(null);
    setMessages([]);
    setInput("");
    setClosed(false);
    setPendingField(null);
    setFieldError(null);
    setShowSignInCta(false);
    // Re-focus the input so the visitor can type immediately.
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: "user",
      content: text,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setSending(true);
    // Visitor chose to type free text instead of using the inline
    // field — clear any pending field so it doesn't linger.
    setPendingField(null);
    setFieldError(null);

    const res = await sendAnonAiChatMessage({
      message: text,
      sessionId,
      leadId,
    });
    setSending(false);

    if (res.success && res.data) {
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        sessionStorage.setItem(SESSION_STORAGE_KEY, res.data.session_id);
      }
      if (res.data.lead_id && res.data.lead_id !== leadId) {
        setLeadId(res.data.lead_id);
        sessionStorage.setItem(LEAD_STORAGE_KEY, res.data.lead_id);
      }
      if (res.data.should_redirect_to_signin) {
        setShowSignInCta(true);
      }
      if (res.data.should_close) {
        setClosed(true);
      }
      setPendingField(res.data.pending_field || null);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: res.data!.response || "(empty response)",
          timestamp: Date.now(),
        },
      ]);
    } else {
      setMessages((prev) => [
        ...prev,
        {
          id: `e-${Date.now()}`,
          role: "system",
          content: res.message || "Sorry, something went wrong. Please try again.",
          timestamp: Date.now(),
        },
      ]);
    }
  }, [input, sending, sessionId, leadId]);

  /**
   * Visitor submitted a value via the inline structured input. Goes
   * to the backend as `field_response` for deterministic apply +
   * validation. On 400 we surface the inline error and keep the
   * field rendered so they can correct it.
   */
  const handleFieldSubmit = useCallback(
    async (rawValue: string) => {
      if (!pendingField || sending) return;
      const fieldName = pendingField.name;
      const fieldLabel = pendingField.label;

      // Echo a user-message bubble showing what they entered, so the
      // chat reads naturally as "you provided X" → "got it, next is Y".
      const userMsg: ChatMessage = {
        id: `u-${Date.now()}`,
        role: "user",
        content: `${fieldLabel}: ${rawValue}`,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setSending(true);
      setFieldError(null);

      const res = await sendAnonAiChatMessage({
        sessionId,
        leadId,
        fieldResponse: { name: fieldName, value: rawValue },
      });
      setSending(false);

      // Validation error: keep the field rendered, show inline error,
      // strip the optimistic user bubble we just added.
      if (!res.success && res.data?.field_error) {
        setFieldError(res.data.field_error.error);
        setMessages((prev) => prev.filter((m) => m.id !== userMsg.id));
        return;
      }

      if (res.success && res.data) {
        if (res.data.session_id && res.data.session_id !== sessionId) {
          setSessionId(res.data.session_id);
          sessionStorage.setItem(SESSION_STORAGE_KEY, res.data.session_id);
        }
        if (res.data.lead_id && res.data.lead_id !== leadId) {
          setLeadId(res.data.lead_id);
          sessionStorage.setItem(LEAD_STORAGE_KEY, res.data.lead_id);
        }
        if (res.data.should_redirect_to_signin) {
          setShowSignInCta(true);
        }
        if (res.data.should_close) {
          setClosed(true);
        }
        // The new pending_field (or null) replaces this one.
        setPendingField(res.data.pending_field || null);
        setMessages((prev) => [
          ...prev,
          {
            id: `a-${Date.now()}`,
            role: "assistant",
            content: res.data!.response || "(empty response)",
            timestamp: Date.now(),
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: `e-${Date.now()}`,
            role: "system",
            content: res.message || "Sorry, something went wrong. Please try again.",
            timestamp: Date.now(),
          },
        ]);
      }
    },
    [pendingField, sending, sessionId, leadId]
  );

  /**
   * Called by AnonChatSigninField after a successful inline sign-in.
   * Token + Redux state are already updated by that component. Here
   * we just continue the chat: send a synthetic continuation turn so
   * the AI acknowledges the sign-in (now with member context) and
   * proceeds. The auth header is now present on the public endpoint
   * call, which causes the orchestrator to switch to authenticated
   * mode and the controller to backfill membership_id on the
   * conversation row (so it shows up in their regular chat history).
   */
  const handleAuthSuccess = useCallback(async () => {
    if (sending) return;
    setPendingField(null);
    setFieldError(null);
    // Render a small inline confirmation in the transcript so the
    // visitor sees something happened.
    setMessages((prev) => [
      ...prev,
      {
        id: `s-${Date.now()}`,
        role: "system",
        content: "Signed in. Continuing where we left off…",
        timestamp: Date.now(),
      },
    ]);
    setSending(true);
    const res = await sendAnonAiChatMessage({
      message:
        "(I just signed in to my existing account. Please continue our conversation with my member context.)",
      sessionId,
      leadId,
    });
    setSending(false);
    if (res.success && res.data) {
      if (res.data.session_id && res.data.session_id !== sessionId) {
        setSessionId(res.data.session_id);
        sessionStorage.setItem(SESSION_STORAGE_KEY, res.data.session_id);
      }
      setPendingField(res.data.pending_field || null);
      setMessages((prev) => [
        ...prev,
        {
          id: `a-${Date.now()}`,
          role: "assistant",
          content: res.data!.response || "(empty response)",
          timestamp: Date.now(),
        },
      ]);
    }
  }, [sending, sessionId, leadId]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg text-[var(--cc-action-text)] hover:shadow-xl transition-all"
          style={{ backgroundColor: accentColor }}
          aria-label="Open chat"
        >
          <MessageCircle size={20} />
          <span className="font-medium text-sm">Chat with us</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-3rem)] h-[560px] max-h-[calc(100vh-3rem)] flex flex-col rounded-xl shadow-2xl border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)}`}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-4 py-3 border-b ${getLightBorder(isDarkMode)} rounded-t-xl ${getPrimaryText(isDarkMode)}`}
          >
            <div className="flex items-center gap-2">
              <MessageCircle size={16} />
              <span className="font-semibold text-sm">
                {businessName || "Customer Service"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                // Clear the active-session flag so future renders
                // pick the auth-appropriate widget (FloatingChatGate
                // resumes its normal logic).
                try { sessionStorage.removeItem(ACTIVE_SESSION_KEY); } catch {}
              }}
              className={`p-1 rounded ${isDarkMode ? "hover:bg-white/10" : "hover:bg-black/5"}`}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          {/* Tenant disclosure banner (compliance / transparency). */}
          {disclosureText && disclosureText.trim().length > 0 && (
            <div
              className={`px-3 py-2 text-[11px] leading-snug border-b ${getLightBorder(isDarkMode)} ${
                isDarkMode ? "bg-gray-800/60 text-gray-300" : "bg-amber-50 text-amber-800"
              }`}
            >
              {disclosureText}
            </div>
          )}

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.map((m) => {
              if (m.role === "system") {
                return (
                  <div key={m.id} className="flex justify-center">
                    <div className={`text-xs px-3 py-1.5 rounded-full ${isDarkMode ? "bg-gray-700/60 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                      {m.content}
                    </div>
                  </div>
                );
              }
              const isUser = m.role === "user";
              return (
                <div
                  key={m.id}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-3 py-2 rounded-lg text-sm ${
                      isUser
                        ? "bg-gray-700 text-white dark:bg-gray-600 whitespace-pre-wrap"
                        : isDarkMode
                        ? "bg-gray-800 text-gray-100 anon-chat-md"
                        : "bg-gray-100 text-gray-900 anon-chat-md"
                    }`}
                  >
                    {isUser ? (
                      m.content
                    ) : (
                      // Assistant turns may include light markdown
                      // (bold for program names, paragraphs, simple
                      // lists). Render via react-markdown. Inline
                      // styling via a wrapper class targets the
                      // limited tags we expect (p / ul / li / strong)
                      // — keeps the typing simple while still giving
                      // bullets and bold their proper appearance.
                      <div className="anon-chat-md [&>p]:mb-1 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:ml-4 [&>ol]:list-decimal [&>ol]:ml-4 [&_li]:mb-0.5 [&_strong]:font-semibold [&_a]:underline">
                        <ReactMarkdown>{m.content}</ReactMarkdown>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {sending && (
              <div className="flex justify-start">
                <div className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${isDarkMode ? "bg-gray-800 text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                  <Loader2 size={14} className="animate-spin" /> Typing…
                </div>
              </div>
            )}

            {showSignInCta && (
              <div className="flex justify-start">
                <a
                  href={signInPath}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-[var(--cc-action-text)] shadow hover:shadow-md transition"
                  style={{ backgroundColor: accentColor }}
                >
                  Sign in to your account →
                </a>
              </div>
            )}
          </div>

          {/* Input area — swaps between the structured field (when
              the backend requested a typed lead field) and the
              free-text textarea (otherwise). One or the other, never
              both — keeps the visitor focused on the requested
              input and avoids two parallel places to type. */}
          <div className={`border-t ${getLightBorder(isDarkMode)}`}>
            {pendingField && !closed ? (
              <>
                <AnonChatStructuredField
                  field={pendingField}
                  accentColor={accentColor}
                  disabled={sending}
                  errorMessage={fieldError}
                  onSubmit={handleFieldSubmit}
                  onAuthSuccess={handleAuthSuccess}
                />
                {/* Escape hatch — visitor can drop the structured ask
                    and free-type if they'd rather. Clears
                    pendingField; the textarea swaps back in.
                    Frontend-only; the LLM still has the prior
                    question in chat history. */}
                <div className="px-3 pb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPendingField(null);
                      setFieldError(null);
                    }}
                    className={`text-[11px] underline ${
                      isDarkMode ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    Type a message instead
                  </button>
                </div>
                <p className={`px-3 pb-2 text-[10px] ${getMutedText(isDarkMode)}`}>
                  Powered by Club Central AI
                </p>
              </>
            ) : (
              <div className="px-3 py-2">
                {closed ? (
                  // Conversation has ended (escalated, capped, etc.) — give
                  // the visitor a way to start a fresh thread instead of
                  // a dead-end disabled input.
                  <>
                    <div className={`text-xs mb-2 ${getMutedText(isDarkMode)} text-center`}>
                      This conversation has ended.
                    </div>
                    <button
                      type="button"
                      onClick={handleStartNewChat}
                      className="w-full p-2 rounded text-[var(--cc-action-text)] text-sm font-medium"
                      style={{ backgroundColor: accentColor }}
                    >
                      Start a new chat
                    </button>
                    <p className={`text-[10px] mt-2 text-center ${getMutedText(isDarkMode)}`}>
                      Powered by Club Central AI
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-end gap-2">
                      <textarea
                        aria-label="Message"
                        ref={inputRef}
                        rows={1}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type your message…"
                        disabled={sending}
                        className={`flex-1 p-2 rounded border text-sm resize-none ${getInputStyles(isDarkMode)}`}
                        style={{ minHeight: "38px", maxHeight: "120px" }}
                      />
                      <button
                        type="button"
                        onClick={handleSend}
                        disabled={sending || !input.trim()}
                        className="p-2 rounded text-[var(--cc-action-text)] disabled:opacity-50"
                        style={{ backgroundColor: accentColor }}
                        aria-label="Send"
                      >
                        <Send size={16} />
                      </button>
                    </div>
                    <p className={`text-[10px] mt-1 ${getMutedText(isDarkMode)}`}>
                      <span className={getSecondaryText(isDarkMode)}>{getPrimaryText(isDarkMode) ? "" : ""}</span>
                      Powered by Club Central AI · Press Enter to send
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AnonymousAIChat;
